import React, { useEffect, useState } from 'react';
import {
  FaAddressCard,
  FaCube,
  FaExchangeAlt,
  FaSignInAlt,
  FaSignOutAlt,
  FaChartLine,
  FaSignal,
  FaNetworkWired,
  FaHashtag, FaDollarSign 
} from "react-icons/fa";

// Chart.js v3+ imports
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { fetchNetworkStats, fetchTableRecords, fetchHighestPriceExchange } from '../services/apiService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const formatHashRate = (hashRate) => {
  const units = ['H/s', 'K/s', 'M/s', 'G/s', 'T/s', 'P/s', 'E/s'];
  let index = 0;

  while (hashRate >= 1000 && index < units.length - 1) {
    hashRate /= 1000;
    index++;
  }

  return `${hashRate.toFixed(2)} ${units[index]}`;
};

const formatToFourDecimals = (value) => {
  return parseFloat(value).toFixed(4);
};

const OverviewCard = () => {
  const [tableData, setTableData] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [highestPrice, setHighestPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState('transactions');

  useEffect(() => {
    Promise.all([fetchTableRecords(), fetchNetworkStats(), fetchHighestPriceExchange()])
      .then(([tableRecords, netStats, highestPriceData]) => {
        setTableData(tableRecords);
        setNetworkData(netStats);
        setHighestPrice(highestPriceData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching data:', err);
        setLoading(false);
      });
  }, []);

  if (loading) return <div>Loading data...</div>;
  if (!tableData) return <div>No table data available.</div>;
  if (!networkData) return <div>No network data available.</div>;

  // Data desde /table-records
  const {
    addresses,
    // eslint-disable-next-line no-unused-vars
    blocks,
    transactions,
    transaction_inputs,
    transaction_outputs,
    transactions_last_14_days,
    difficulty: difficultyDataArray 
  } = tableData;

  const points = transactions_last_14_days?.points || [];
  const totalLast14 = transactions_last_14_days?.total_transactions;

  // Data desde /network-stats
  const {
    difficulty,
    connectionCount,
    networkHashPs,
    blockCount
  } = networkData;

  // ==============================================
  //   GRÁFICA #1: Transacciones (últimos 14 días)
  // ==============================================
  const transactionsChartData = {
    labels: points.map((p) => new Date(p.date).toLocaleDateString()),
    datasets: [
      {
        label: 'Transactions/Day',
        data: points.map((p) => parseInt(p.count, 10)),
        borderColor: '#4F46E5',
        backgroundColor: 'rgba(79,70,229,0.2)',
        fill: true,
        tension: 0.2
      }
    ]
  };

  const transactionsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

// ==============================
//   GRÁFICA #2: DIFICULTAD
// ==============================
const difficultyChartData = {
  datasets: [
    {
      label: 'Network Difficulty',
      data: difficultyDataArray?.map(item => ({
        x: item.height,
        y: item.difficulty
      })) || [],
      borderColor: 'rgba(255, 99, 132, 1)',
      backgroundColor: 'rgba(255, 99, 132, 0.2)',
      fill: true,
      tension: 0.3,
      pointRadius: 0,
      pointHoverRadius: 3,
      borderWidth: 2
    }
  ]
};

const difficultyChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'linear',
      title: {
        display: true,
        text: 'Block Height'
      }
    },
    y: {
      type: 'linear',
      title: {
        display: true,
        text: 'Difficulty'
      },
    }
  },
  plugins: {
    legend: { display: false },
    title: { display: false }
  }
};

// ==============================
//   GRÁFICA #3: HASHRATE
// ==============================
const hashrateChartData = {
  datasets: [
    {
      label: 'Network Hashrate (H/s)',
      data: difficultyDataArray?.map(item => ({
        x: item.height,
        y: item.hashrate
      })) || [],
      borderColor: 'rgba(54, 162, 235, 1)',
      backgroundColor: 'rgba(54, 162, 235, 0.2)',
      fill: true,
      tension: 0.3,
      pointRadius: 0,
      pointHoverRadius: 3,
      borderWidth: 2
    }
  ]
};

const hashrateChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'linear',
      title: {
        display: true,
        text: 'Block Height'
      }
    },
    y: {
      type: 'linear',
      title: {
        display: true,
        text: 'Hashrate (H/s)'
      },
       beginAtZero: true,
    }
  },
  plugins: {
    legend: { display: false },
    title: { display: false }
  }
};

  return (
    <div className="bg-white shadow rounded p-4 mb-6">
      <h2 className="text-2xl font-bold mb-4">Blockchain Overview</h2>

      <div className="flex flex-col md:flex-row">
        
        {/* LADO IZQUIERDO: Datos */}
        <div className="md:w-1/2 md:pr-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center">
              <FaAddressCard className="text-blue-500 mr-2" />
              <strong>Addresses:</strong> {addresses}
            </div>

            <div className="flex flex-col items-center justify-center bg-green-100 border border-green-400 text-green-800 font-semibold px-4 py-2 rounded-lg shadow-md w-full sm:w-auto">
              <div className="flex items-center">
                <FaDollarSign className="text-green-600 mr-1 text-sm" />
                <strong className="text-sm"> </strong> 
                <span className="ml-1 text-sm text-green-700">
                  {highestPrice ? `${highestPrice.last_price}` : 'Loading...'}
                </span>
              </div>
              {highestPrice?.exchange_name && (
                <span className="text-[10px] text-gray-600 mt-1">
                  {highestPrice.exchange_name}
                </span>
              )}
            </div>

            <div className="flex items-center">
              <FaExchangeAlt className="text-green-500 mr-2" />
              <strong>Transactions:</strong> {transactions}
            </div>
            <div className="flex items-center">
              <FaChartLine className="text-teal-500 mr-2" />
              <strong>Tx (Last 14 days):</strong> {totalLast14}
            </div>
            <div className="flex items-center">
              <FaSignInAlt className="text-red-500 mr-2" />
              <strong>Tx Inputs:</strong> {transaction_inputs}
            </div>
            <div className="flex items-center">
              <FaSignOutAlt className="text-orange-500 mr-2" />
              <strong>Tx Outputs:</strong> {transaction_outputs}
            </div>
            <div className="flex items-center">
              <FaSignal className="text-indigo-500 mr-2" />
              <strong>Difficulty:</strong> {formatToFourDecimals(difficulty)}
            </div>
            <div className="flex items-center">
              <FaHashtag className="text-gray-500 mr-2" />
              <strong>Hash Rate:</strong> {formatHashRate(networkHashPs)}
            </div>
            <div className="flex items-center">
              <FaNetworkWired className="text-yellow-500 mr-2" />
              <strong>Connections:</strong> {connectionCount}
            </div>
            <div className="flex items-center">
              <FaCube className="text-purple-500 mr-2" />
              <strong>Block:</strong> {blockCount}
            </div>
          </div>
        </div>

        {/* LADO DERECHO: Gráficas */}
        <div className="md:w-1/2 h-40 md:h-40 mt-4 md:mt-0">
          <div className="flex justify-center space-x-2 mb-2">
            <button
              onClick={() => setChartView('transactions')}
              className={`px-3 py-1 text-sm rounded border 
                ${chartView === 'transactions' ? 'bg-gray-300' : 'bg-white hover:bg-gray-100'}`}
            >
              Transactions
            </button>
            <button
              onClick={() => setChartView('difficulty')}
              className={`px-3 py-1 text-sm rounded border 
                ${chartView === 'difficulty' ? 'bg-gray-300' : 'bg-white hover:bg-gray-100'}`}
            >
              Difficulty
            </button>
            <button
              onClick={() => setChartView('hashrate')}
              className={`px-3 py-1 text-sm rounded border 
                ${chartView === 'hashrate' ? 'bg-gray-300' : 'bg-white hover:bg-gray-100'}`}
            >
              Hashrate
            </button>
          </div>

          {/* Gráfica #1: Transacciones */}
          {chartView === 'transactions' && (
            <>
              <h2 className="text-center text-md mb-1">
                TRANSACTION HISTORY (Last 14 days)
              </h2>
              <Line data={transactionsChartData} options={transactionsChartOptions} />
            </>
          )}

          {/* Gráfica #2: Dificultad */}
          {chartView === 'difficulty' && (
            <>
              <h2 className="text-center text-md mb-1">
                NETWORK DIFFICULTY (Last 720 blocks)
              </h2>
              <Line data={difficultyChartData} options={difficultyChartOptions} />
            </>
          )}

          {/* Gráfica #3: Hashrate */}
          {chartView === 'hashrate' && (
            <>
              <h2 className="text-center text-md mb-1">
                NETWORK HASHRATE (Last 720 blocks)
              </h2>
              <Line data={hashrateChartData} options={hashrateChartOptions} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewCard;
